"use client"

import { motion, useReducedMotion } from "framer-motion"

const easeOut = [0.22, 1, 0.36, 1] as const

/** Quiet signal: life moves across places; MAATII accretes meaning along the way. */
const trail =
  [
    { place: "Addis Ababa", note: "A family evening at home" },
    { place: "Adama", note: "Coffee shared after a long day" },
    { place: "Minneapolis", note: "The same circle, in a different place" },
  ] as const

export function MarketingPlacesTrail() {
  const reduceMotion = useReducedMotion()

  return (
    <section className="relative w-full min-w-0 py-30 sm:py-38">
      <motion.div
        className="mx-auto max-w-[70rem] rounded-card-shell border border-border/35 bg-card/24 px-[var(--space-5)] py-[var(--space-2xl)] sm:px-[var(--space-10)] sm:py-[var(--space-10)]"
        initial={reduceMotion ? false : { opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: reduceMotion ? 0 : 0.55, ease: easeOut }}
      >
        <div className="grid gap-10 lg:grid-cols-12 lg:items-start lg:gap-10">
          <div className="lg:col-span-5 lg:pt-1">
            <p className="type-marketing-eyebrow mb-[var(--space-xs)]">Beyond one night</p>
            <h2 className="type-section-title mb-[var(--space-sm)]">
              Threads of place, in order
            </h2>
            <p className="type-hero-subtitle max-w-[32ch] text-foreground/82">
              Gatherings are rarely isolated. Trips, returns home, and rituals in familiar rooms leave a trail —
              MAATII is built so that trail can grow with you, without turning into a travel brochure.
            </p>
          </div>

          <div className="relative flex flex-col gap-0 lg:col-span-7 lg:pl-1">
            <div
              className="pointer-events-none absolute bottom-3 left-[0.6rem] top-3 w-px bg-border/35 sm:left-[0.65rem]"
              aria-hidden
            />
            <ol className="relative space-y-[var(--space-lg)] sm:space-y-[var(--space-xl)]">
              {trail.map((stop, i) => (
                <li key={stop.place} className="flex gap-[var(--space-md)] pl-[var(--space-2xs)] sm:gap-[var(--space-5)]">
                  <div className="relative z-[1] flex h-5 w-5 shrink-0 items-center justify-center pt-0.5 sm:h-6 sm:w-6">
                    <span className="size-2.5 rounded-full border border-border/50 bg-background shadow-sm shadow-black/30" />
                  </div>
                  <div className="min-w-0 pt-0.5">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="type-body font-medium">{stop.place}</span>
                      {i < trail.length - 1 ? (
                        <span className="hidden text-muted-foreground/50 sm:inline" aria-hidden>
                          —
                        </span>
                      ) : null}
                    </div>
                    <p className="type-body-sm mt-1 text-foreground/78">{stop.note}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="type-caption mt-8 sm:mt-10">
              No public map, no check-ins — just your chronology, when you choose to shape it.
            </p>
          </div>
        </div>
      </motion.div>
    </section>
  )
}
